import { Color3 } from "@babylonjs/core/Maths/math.color";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { Util } from '../utils/Util';
import { WallEnding, Wall } from '../mdl/Wall';
import { SnapLine, SnapPoint } from '@egr/wcf/modules/core/tool';
import { VECTOR2_X, VECTOR2_Y, intersectLineLine2, isParallel2 } from '@egr/wcf/modules/core/utils/MathUtils';
export class WallSnapping {
    constructor(pRoomManager) {
        this.mRoomManager = pRoomManager;
        this.mApp = pRoomManager.app;
        this.mSnapAxes = new Array();
        this.mSnapPoints = new Array();
        this.mActiveSnapLines = [null, null];
        this.mSnapLineFeedbacks = [null, null];
        this.mWall = null;
        this.mBasePoint = null;
        this.mBasePointX = null;
        this.mBasePointY = null;
        this.mBaseAxis = null;
        this.mBaseAxisX = null;
        this.mBaseAxisY = null;
    }
    // tslint:disable-next-line:cyclomatic-complexity
    prepareSnapping(pMoveMode, pWall, pBaseEnding) {
        this.clear();
        this.mWall = pWall != null ? pWall : null;
        if (this.mWall != null) {
            this.mBasePoint = pBaseEnding === WallEnding.Start ? this.mWall.startPoint : this.mWall.endPoint;
            let tBasePoint3d = Util.to3d(this.mBasePoint);
            this.mBasePointX = this.mBasePoint.add(VECTOR2_X);
            this.mBasePointY = this.mBasePoint.add(VECTOR2_Y);
            this.mBaseAxis = new SnapLine(Util.to3d(this.mWall.startPoint), Util.to3d(this.mWall.endPoint));
            this.mSnapAxes.push(this.mBaseAxis);
            //x and y axes
            this.mBaseAxisX = new SnapLine(tBasePoint3d, Util.to3d(this.mBasePointX));
            if (pMoveMode != WallSnapping.Mode.MoveWall)
                this.mSnapAxes.push(this.mBaseAxisX);
            this.mBaseAxisY = new SnapLine(tBasePoint3d, Util.to3d(this.mBasePointY));
            if (pMoveMode != WallSnapping.Mode.MoveWall)
                this.mSnapAxes.push(this.mBaseAxisY);
        }
        else {
            this.mBasePoint = null;
            this.mBasePointX = null;
            this.mBasePointY = null;
            this.mBaseAxis = null;
            this.mBaseAxisX = null;
            this.mBaseAxisY = null;
        }
        for (let tWall of this.mRoomManager.walls) {
            if (tWall === this.mWall)
                continue;
            this.addWallSnap(pMoveMode, tWall);
            this.addWallEndingSnap(pMoveMode, tWall, WallEnding.Start);
            this.addWallEndingSnap(pMoveMode, tWall, WallEnding.End);
        }
        if (pMoveMode == WallSnapping.Mode.MoveWall) {
            for (let tWall1 of this.mRoomManager.walls) {
                for (let tWall2 of this.mRoomManager.walls) {
                    if (tWall1 === tWall2)
                        continue;
                    this.addIntersectionSnap(pMoveMode, tWall1, tWall2);
                }
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    snap(pPos1, pView) {
        if (!this.mRoomManager.app.settings.snapping.boundingBoxEnabled ||
            !this.mRoomManager.app.settings.snapping.gridEnabled) {
            let tRes = {};
            tRes.snap_Type = WallSnapping.SnapType.Nothing;
            tRes.vector_Point = pPos1;
            return (tRes);
        }
        //snap on points
        let tPoint = SnapPoint.Snap(pPos1, this.mSnapPoints, pView);
        if (tPoint != null) {
            this.updateSnapFeedback(0, tPoint._snapLine1);
            this.updateSnapFeedback(1, tPoint._snapLine2);
            if (this.mWall !== null) {
                this.mWall.wallSnapped(Wall.WallSnapState.Point);
            }
            let tRes = {};
            tRes.snap_Type = WallSnapping.SnapType.Point;
            tRes.vector_Point = tPoint;
            return (tRes);
        }
        //snap on wall endings
        let tPos2d = Util.to2d(pPos1);
        for (let tWall of this.mRoomManager.walls) {
            if (tWall === this.mWall)
                continue;
            let tHT = tWall.thickness * 0.5;
            let tDist = Vector2.Distance(tPos2d, tWall.startPoint);
            if (tDist <= tHT) {
                if (this.mWall !== null) {
                    this.mWall.wallSnapped(Wall.WallSnapState.Point);
                }
                let tRes = {};
                tRes.snap_Type = WallSnapping.SnapType.Point;
                tRes.vector_Point = Util.to3d(tWall.startPoint);
                return (tRes);
            }
            tDist = Vector2.Distance(tPos2d, tWall.endPoint);
            if (tDist <= tHT) {
                if ((this.mWall != null) &&
                    (this.mWall.startCxn.wall == tWall || this.mWall.endCxn.wall == tWall)) {
                    let tRes = {};
                    tRes.snap_Type = WallSnapping.SnapType.Nothing;
                    tRes.vector_Point = pPos1;
                    return (tRes);
                }
                if (this.mWall !== null) {
                    this.mWall.wallSnapped(Wall.WallSnapState.Point);
                }
                let tRes = {};
                tRes.snap_Type = WallSnapping.SnapType.Point;
                tRes.vector_Point = Util.to3d(tWall.endPoint);
                return (tRes);
            }
        }
        //snap on lines
        let tResult = SnapLine.Snap(pPos1, this.mSnapAxes, pView);
        if (tResult != null) {
            this.updateSnapFeedback(0, tResult.line);
            this.updateSnapFeedback(1, null);
            if (this.mWall !== null) {
                let tsnapDir = this.getSnapDirection(tResult.line.direction);
                this.mWall.wallSnapped(tsnapDir);
            }
            let tRes = {};
            tRes.snap_Type = WallSnapping.SnapType.OnLine;
            tRes.vector_Point = tResult.pos;
            return (tRes);
        }
        this.updateSnapFeedback(0, null);
        this.updateSnapFeedback(1, null);
        if (this.mWall !== null) {
            this.mWall.wallSnapped(Wall.WallSnapState.None);
        }
        let tRes = {};
        tRes.snap_Type = WallSnapping.SnapType.Nothing;
        tRes.vector_Point = pPos1;
        return (tRes);
    }
    clear() {
        this.mWall = null;
        this.mSnapAxes = new Array();
        this.mSnapPoints = new Array();
        this.updateSnapFeedback(0, null);
        this.updateSnapFeedback(1, null);
    }
    getSnapDirection(pDirection) {
        let tRes = Wall.WallSnapState.None;
        let tangle = Math.atan2(pDirection.z, pDirection.x);
        let tdegrees = 180 * tangle / Math.PI;
        if (Util.numbersEqual(tdegrees, 0, 0.001) || Util.numbersEqual(tdegrees, 180, 0.001))
            tRes = Wall.WallSnapState.Horizontal;
        if (Util.numbersEqual(tdegrees, 90, 0.001) || Util.numbersEqual(tdegrees, -90, 0.001))
            tRes = Wall.WallSnapState.Vertical;
        return (tRes);
    }
    checkLargeAreaIntersection(pThisWall, pOtherWall) {
        let tRes = null;
        if (pThisWall === null || pOtherWall === null)
            return (tRes);
        let t_thisWall_StartPoint = pThisWall.startPoint;
        let t_thisWall_EndPoint = pThisWall.endPoint;
        let t_thisWall_Direction = pThisWall.wallDirection;
        let t_otherWall_StartPoint = pOtherWall.startPoint;
        let t_otherWall_EndPoint = pOtherWall.endPoint;
        let t_otherWall_Direction = pOtherWall.wallDirection;
        t_thisWall_StartPoint = t_thisWall_StartPoint.subtract((t_thisWall_Direction).scale(1000));
        t_thisWall_EndPoint = t_thisWall_EndPoint.add(t_thisWall_Direction.scale(1000));
        t_otherWall_StartPoint = t_otherWall_StartPoint.subtract((t_otherWall_Direction).scale(1000));
        t_otherWall_EndPoint = t_otherWall_EndPoint.add(t_otherWall_Direction.scale(1000));
        tRes = intersectLineLine2(t_thisWall_StartPoint, t_thisWall_EndPoint, t_otherWall_StartPoint, t_otherWall_EndPoint);
        return (tRes);
    }
    addIntersectionSnap(pMoveMode, pWall1, pWall2) {
        if (this.mWall != null && pMoveMode == WallSnapping.Mode.MoveWall &&
            (pWall1 == this.mWall.endCxn.wall || pWall1 == this.mWall.startCxn.wall))
            return;
        let tSnapAxis = new SnapLine(Util.to3d(pWall1.startPoint), Util.to3d(pWall1.endPoint));
        this.mSnapAxes.push(tSnapAxis);
        if (pWall1 === null || pWall2 === null)
            return;
        if (!isParallel2(pWall1.startPoint, pWall1.endPoint, pWall2.startPoint, pWall2.endPoint)) {
            let tIsec = this.checkLargeAreaIntersection(pWall1, pWall2);
            if (tIsec != null) {
                let tSnapAxis1 = new SnapLine(Util.to3d(pWall2.startPoint), Util.to3d(pWall2.endPoint));
                let tSnapPoint = Util.to3d(tIsec);
                tSnapPoint._snapLine1 = tSnapAxis;
                tSnapPoint._snapLine2 = tSnapAxis1;
                this.mSnapPoints.push(tSnapPoint);
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    addWallSnap(pMoveMode, pWall) {
        if (this.mWall != null && pMoveMode == WallSnapping.Mode.MoveWall &&
            (pWall == this.mWall.endCxn.wall || pWall == this.mWall.startCxn.wall))
            return;
        let tSnapAxis = new SnapLine(Util.to3d(pWall.startPoint), Util.to3d(pWall.endPoint));
        this.mSnapAxes.push(tSnapAxis);
        //intersection with base axes
        if (this.mWall != null) {
            if (!isParallel2(this.mWall.startPoint, this.mWall.endPoint, pWall.startPoint, pWall.endPoint)) {
                // let tIsec = intersectLineLine2(this.mWall.startPoint, this.mWall.endPoint, pWall.startPoint, pWall.endPoint);
                let tIsec = this.checkLargeAreaIntersection(this.mWall, pWall);
                if (tIsec != null) {
                    let tSnapPoint = Util.to3d(tIsec);
                    tSnapPoint._snapLine1 = tSnapAxis;
                    tSnapPoint._snapLine2 = this.mBaseAxis;
                    this.mSnapPoints.push(tSnapPoint);
                }
            }
            if (this.mBasePoint != null && this.mBasePointX != null && this.mBasePointY != null) {
                if (!isParallel2(this.mBasePoint, this.mBasePointX, pWall.startPoint, pWall.endPoint)) {
                    let tIsec = intersectLineLine2(this.mBasePoint, this.mBasePointX, pWall.startPoint, pWall.endPoint);
                    if (tIsec != null) {
                        let tSnapPoint = Util.to3d(tIsec);
                        tSnapPoint._snapLine1 = tSnapAxis;
                        tSnapPoint._snapLine2 = this.mBaseAxisX;
                        this.mSnapPoints.push(tSnapPoint);
                    }
                }
                if (!isParallel2(this.mBasePoint, this.mBasePointY, pWall.startPoint, pWall.endPoint)) {
                    let tIsec = intersectLineLine2(this.mBasePoint, this.mBasePointY, pWall.startPoint, pWall.endPoint);
                    if (tIsec != null) {
                        let tSnapPoint = Util.to3d(tIsec);
                        tSnapPoint._snapLine1 = tSnapAxis;
                        tSnapPoint._snapLine2 = this.mBaseAxisY;
                        this.mSnapPoints.push(tSnapPoint);
                    }
                }
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    addWallEndingSnap(pMoveMode, pWall, pEnding) {
        if (this.mWall != null && pMoveMode == WallSnapping.Mode.MoveWall &&
            (pWall == this.mWall.endCxn.wall || pWall == this.mWall.startCxn.wall))
            return;
        let tPoint = pEnding === WallEnding.Start ? pWall.startPoint : pWall.endPoint;
        let tPoint3d = Util.to3d(tPoint);
        let tPointX = tPoint.add(VECTOR2_X);
        let tPointY = tPoint.add(VECTOR2_Y);
        //x and y axes
        let tSnapAxisX = new SnapLine(tPoint3d, Util.to3d(tPointX));
        this.mSnapAxes.push(tSnapAxisX);
        let tSnapAxisY = new SnapLine(tPoint3d, Util.to3d(tPointY));
        this.mSnapAxes.push(tSnapAxisY);
        //ending
        this.mSnapPoints.push(Util.to3d(tPoint));
        //intersection with base axes
        if (this.mWall != null) {
            if (!isParallel2(this.mWall.startPoint, this.mWall.endPoint, tPoint, tPointX)) {
                let tIsec = intersectLineLine2(this.mWall.startPoint, this.mWall.endPoint, tPoint, tPointX);
                if (tIsec != null) {
                    let tSnapPoint = Util.to3d(tIsec);
                    tSnapPoint._snapLine1 = tSnapAxisX;
                    tSnapPoint._snapLine2 = this.mBaseAxis;
                    this.mSnapPoints.push(tSnapPoint);
                }
            }
            if (!isParallel2(this.mWall.startPoint, this.mWall.endPoint, tPoint, tPointY)) {
                let tIsec = intersectLineLine2(this.mWall.startPoint, this.mWall.endPoint, tPoint, tPointY);
                if (tIsec != null) {
                    let tSnapPoint = Util.to3d(tIsec);
                    tSnapPoint._snapLine1 = tSnapAxisY;
                    tSnapPoint._snapLine2 = this.mBaseAxis;
                    this.mSnapPoints.push(tSnapPoint);
                }
            }
            if (this.mBasePoint != null && this.mBasePointX != null && this.mBasePointY != null) {
                let tIsec = intersectLineLine2(this.mBasePoint, this.mBasePointX, tPoint, tPointY);
                if (tIsec != null) {
                    let tSnapPoint = Util.to3d(tIsec);
                    tSnapPoint._snapLine1 = tSnapAxisY;
                    tSnapPoint._snapLine2 = this.mBaseAxisX;
                    this.mSnapPoints.push(tSnapPoint);
                }
                tIsec = intersectLineLine2(this.mBasePoint, this.mBasePointY, tPoint, tPointX);
                if (tIsec != null) {
                    let tSnapPoint = Util.to3d(tIsec);
                    tSnapPoint._snapLine1 = tSnapAxisX;
                    tSnapPoint._snapLine2 = this.mBaseAxisY;
                    this.mSnapPoints.push(tSnapPoint);
                }
            }
        }
    }
    updateSnapFeedback(pIndex, pSnapLine) {
        //dispose current feedback
        if ((pSnapLine == null || pSnapLine != this.mActiveSnapLines[pIndex]) &&
            (this.mSnapLineFeedbacks[pIndex] != null)) {
            this.mSnapLineFeedbacks[pIndex].dispose();
            this.mSnapLineFeedbacks[pIndex] = null;
        }
        //create new
        if (pSnapLine != null) {
            if (pSnapLine != this.mActiveSnapLines[pIndex]) {
                this.mActiveSnapLines[pIndex] = pSnapLine;
                this.mSnapLineFeedbacks[pIndex] = this.createSnapAxisFeedback(pSnapLine);
            }
        }
        else {
            this.mActiveSnapLines[pIndex] = null;
        }
    }
    createSnapAxisFeedback(pSnapLine) {
        if (pSnapLine == null)
            return (null);
        let tDir = pSnapLine.point2.subtract(pSnapLine.point1).normalize();
        tDir.scaleInPlace(100);
        let tLine = CreateLines("", {
            points: [pSnapLine.point1.subtract(tDir), pSnapLine.point1.add(tDir)],
            updatable: false,
            instance: null
        }, this.mApp.scene);
        tLine.isPickable = false;
        tLine.layerMask = this.feedbackVisibility;
        tLine.alpha = 0.25;
        tLine.renderingGroupId = 1;
        tLine.color = Color3.Black();
        return (tLine);
    }
}
//add nested types
(function (WallSnapping) {
    let Mode;
    (function (Mode) {
        Mode[Mode["MoveStart"] = 0] = "MoveStart";
        Mode[Mode["MoveEnd"] = 1] = "MoveEnd";
        Mode[Mode["MoveWall"] = 2] = "MoveWall";
    })(Mode = WallSnapping.Mode || (WallSnapping.Mode = {}));
    let SnapType;
    (function (SnapType) {
        SnapType[SnapType["Point"] = 0] = "Point";
        SnapType[SnapType["OnLine"] = 1] = "OnLine";
        SnapType[SnapType["Nothing"] = 2] = "Nothing";
    })(SnapType = WallSnapping.SnapType || (WallSnapping.SnapType = {}));
})(WallSnapping || (WallSnapping = {}));
